import { Modal } from '../../ui/Modal.tsx';
import { describeError } from '../../ui/problemDetails.ts';
import { useItemImageBlob, useObjectUrl } from './useItemImages.ts';

interface PhotoViewerProps {
  groupId: string;
  photo: { id: string; width: number; height: number };
  /** The item whose photo this is, for the title and the alt text. */
  name: string;
  onClose: () => void;
}

/**
 * Looking at the photo attached to a name.
 *
 * <b>The first thing on the shopping path to use a modal</b>, which is worth saying out
 * loud because the codebase says nothing there does. That rule is about confirmations and
 * forms interrupting a flow — a photograph is not either. It wants the top layer so it is
 * not clipped by the list's scroll container, and Escape, the backdrop, focus containment
 * and focus restoration are all free from `<dialog>` rather than being ours to get wrong.
 *
 * The bytes are fetched here rather than with the list: forty rows must not pull forty
 * photographs down a supermarket connection to draw icons nobody has asked to look at.
 */
export function PhotoViewer({ groupId, photo, name, onClose }: PhotoViewerProps) {
  const { data: blob, isPending, error } = useItemImageBlob(groupId, photo.id);
  const url = useObjectUrl(blob);

  return (
    <Modal title={name} onClose={onClose}>
      <div className="photo-viewer">
        {isPending && <p className="muted">Loading the photo…</p>}

        {error && (
          <p className="form-error" role="alert">
            {describeError(error)}
          </p>
        )}

        {url && (
          <img
            className="photo-viewer__image"
            src={url}
            alt={`Photo of ${name}`}
            // From the catalogue, so the dialog is the right shape before the bytes
            // arrive and does not snap about under a thumb when they do.
            width={photo.width}
            height={photo.height}
          />
        )}
      </div>
    </Modal>
  );
}
